import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { OrbChat } from '@/components/OrbChat';
import { LogoOrb } from '@/components/LogoOrb';
import { assistantApi, type OrbNudge } from '@/api/client';
import { ORB_ASK_EVENT, ORB_LIVE_EVENT, raiseLiveNudge, type LiveNudge } from '@/components/orbLive';
import { STUCK_AFTER_MS, stuckOfferFor } from '@/lib/stuck.core';
import { useLocation } from 'react-router-dom';
import { track } from '@/lib/analytics';
import { useUiMode } from '@/hooks/useUiMode';
import { isViewingAsSomeoneElse } from '@/lib/viewAs';
import { isOnboardingActive, useOnboardingActive } from '@/lib/onboarding';

/* The Orb speaking first.
   Guillaume, 2026-09-19: "it won't just stand still waiting for a user to
   request some help". A daily check on the backend (assistant_nudge.go) opens a
   nudge when someone's orders are failing for a reason they can fix; here the
   Orb shows it has something to say, and opening it starts with that message.
   Checked on load and every ten minutes -- the check itself runs once a day, so
   anything faster would only be asking the same question more often. */
const NUDGE_POLL_MS = 10 * 60 * 1000;

/*
 * THE DING. Guillaume 2026-09-19 (#14): "a little Message sound, some sort of
 * Ding, and a little like 1 circled in red next to the Orb when the user have
 * an unread message."
 *
 * Two soft notes made in the browser, so there is no sound file to ship or
 * fail to load. Once per NEW message: the id is remembered, so the poll and a
 * reload do not ding again for the same one. A browser that blocks sound
 * before the first click simply stays silent; the red badge still shows.
 */
const DINGED_KEY = 'copyfi-orb-dinged';

function alreadyDinged(id: string | number): boolean {
    try { return localStorage.getItem(DINGED_KEY) === String(id); } catch { return false; }
}

function rememberDing(id: string | number) {
    try { localStorage.setItem(DINGED_KEY, String(id)); } catch { /* the badge is enough */ }
}

function playDing() {
    try {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const note = (freq: number, at: number) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.0001, ctx.currentTime + at);
            gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + at + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.45);
            osc.connect(gain).connect(ctx.destination);
            osc.start(ctx.currentTime + at);
            osc.stop(ctx.currentTime + at + 0.5);
        };
        note(880, 0);
        note(1318.5, 0.12);
        window.setTimeout(() => { ctx.close().catch(() => undefined); }, 1200);
    } catch { /* no sound available: the badge carries it */ }
}

const INVITE_SEEN_KEY = 'copyfi.orb.inviteSeen';

/** The Orb's corner kept inside the window, with its 96 px body and a small margin. */
function onScreen(p: { x: number; y: number }): { x: number; y: number } {
    const size = 96, m = 8;
    return {
        x: Math.min(Math.max(m, p.x), Math.max(m, window.innerWidth - size - m)),
        y: Math.min(Math.max(m, p.y), Math.max(m, window.innerHeight - size - m)),
    };
}

export function CopyFiOrb() {
    const { t, i18n } = useTranslation('common');
    const { t: tl } = useTranslation('learn');

    /* Default: the bottom-right corner, clear of the content it used to sit
       over (2026-10-09). The button is 96px; 120 leaves a 24px margin. */
    const [pos, setPos] = useState({
        x: Math.max(0, window.innerWidth - 120),
        y: Math.max(0, window.innerHeight - 120),
    });
    const [open, setOpen] = useState(false);
    const [ask, setAsk] = useState<string | null>(null);
    const [askGuide, setAskGuide] = useState<string | undefined>();
    useEffect(() => {
        const onAsk = (e: Event) => {
            const d = (e as CustomEvent<{ question: string; guide?: string }>).detail;
            const q = d?.question;
            if (!q) return;
            setAsk(q);
            setAskGuide(d?.guide);
            setOpen(true);
        };
        window.addEventListener(ORB_ASK_EVENT, onAsk);
        return () => window.removeEventListener(ORB_ASK_EVENT, onAsk);
    }, []);
    const [nudge, setNudge] = useState<OrbNudge | null>(null);
    // The bubble for a LIVE message says what it is about; the daily one keeps its generic line.
    const [liveBubble, setLiveBubble] = useState<string | null>(null);
    /* The first conversation (Guillaume, 2026-10-06): on the first visit the
       Orb gets to know the person (once they tap it). Asked once per load;
       the server says when (never had one, left mid-way, or "not now" three
       days ago) and holds the daily cap. Never while an admin views as
       someone else: that would be the admin answering for them. */
    const [interview, setInterview] = useState(false);
    // Quiet while the welcome chooser, its video or a welcome guide is on screen (lib/onboarding.ts).
    const quiet = useOnboardingActive();
    /* A check-in, when the profile is done but may have changed (Guillaume,
       2026-10-06: "we need to be able to re-evaluate him once in a while").
       The server decides when (time, what they do, or they asked) and the
       answers go to the same interview endpoint; this only picks the opening
       line by why it is happening. */
    const [checkIn, setCheckIn] = useState<string | null>(null);
    const [profileDone, setProfileDone] = useState(false);
    /* Never opened by itself (Guillaume, 2026-10-09: "the Orb conversation
       opens automatically every single time -- remove that"). An offered
       interview or check-in waits for a tap, with a small dot on the bubble as
       the only invitation, shown until the panel is first opened and then never
       again on this device. */
    const [inviteSeen, setInviteSeen] = useState(() => {
        try { return localStorage.getItem(INVITE_SEEN_KEY) === '1'; } catch { return false; }
    });
    const openPanel = (next: boolean) => {
        setOpen(next);
        if (next && interview && !inviteSeen) {
            setInviteSeen(true);
            try { localStorage.setItem(INVITE_SEEN_KEY, '1'); } catch { /* the dot may show once more */ }
        }
    };
    useEffect(() => {
        if (isViewingAsSomeoneElse()) return;
        let alive = true;
        assistantApi.profile()
            .then((r) => {
                if (!alive) return;
                setProfileDone(r.profile?.status === 'done');
                if (r.offer_interview) {
                    setInterview(true);
                    track('orb_interview_offered', r.profile?.status ?? 'new');
                } else if (r.offer_checkin) {
                    setCheckIn(r.checkin_trigger || 'time');
                    setInterview(true);
                    track('orb_checkin_offered', r.checkin_trigger || 'time');
                }
            })
            .catch(() => { /* no interview is the safe reading of a failed check */ });
        return () => { alive = false; };
    }, []);
    /* THE SPEED RUN'S FIRST STEP (Guillaume 2026-10-24): in Lite, a brand-new
       account sees the Orb open by itself once, with its getting-to-know-you
       offer, after any welcome is closed. Pro keeps the 10-09 rule (never by
       itself, a dot only); in Lite this happens once per device, never again. */
    const { mode: uiMode } = useUiMode();
    useEffect(() => {
        if (uiMode !== 'lite' || !interview || checkIn || inviteSeen || quiet || open) return;
        const id = window.setTimeout(() => {
            track('orb_interview_offered', 'lite_auto_open');
            openPanel(true);
        }, 1200);
        return () => window.clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiMode, interview, checkIn, inviteSeen, quiet, open]);
    const requestUpdate = () => {
        track('orb_profile_update_requested');
        assistantApi.checkin('request')
            .then((r) => {
                if (!r.offer_checkin) return;
                setCheckIn('self');
                setInterview(true);
            })
            .catch(() => { /* the button stays; it can be pressed again */ });
    };

    /* Stuck on a screen (Guillaume 10-25: "Orb propose assistance"): two
       minutes on a screen where something gets done, without moving on, and
       the Orb offers a hand with that screen (lib/stuck.core.ts). */
    const { pathname } = useLocation();
    useEffect(() => {
        const offer = stuckOfferFor(pathname);
        if (!offer) return;
        const id = window.setTimeout(() => {
            raiseLiveNudge({ trigger: `stuck-${offer.screen}`, messageKey: `orb.live.stuck.${offer.screen}.message`,
                bubbleKey: `orb.live.stuck.${offer.screen}.bubble`, guide: offer.guide });
        }, STUCK_AFTER_MS);
        return () => window.clearTimeout(id);
    }, [pathname]);

    /* A screen saw somebody get stuck (orbLive.ts): speak now. A live message
       takes the place of an unread daily one for the moment; the daily one is
       still on the server and comes back on the next poll. Its id is negative
       so nothing ever marks it seen on the server -- it was never stored. */
    useEffect(() => {
        const onLive = (e: Event) => {
            const n = (e as CustomEvent<LiveNudge>).detail;
            if (!n) return;
            setNudge({ id: -Date.now(), kind: 'LIVE', trigger: n.trigger, message: tl(n.messageKey, n.values), created_at: new Date().toISOString(), guide: n.guide });
            setLiveBubble(tl(n.bubbleKey, n.values));
            track('assistant_live_shown', n.trigger);
            playDing();
            if (n.open) setOpen(true);
        };
        window.addEventListener(ORB_LIVE_EVENT, onLive);
        return () => window.removeEventListener(ORB_LIVE_EVENT, onLive);
    }, [tl]);

    useEffect(() => {
        let alive = true;
        const check = () =>
            assistantApi.nudge(i18n.language)
                .then((r) => {
                    if (!alive) return;
                    // An unread live message is not replaced by "nothing" from the poll.
                    setNudge((cur) => (cur && cur.kind === 'LIVE' && !r.nudge ? cur : r.nudge));
                    if (r.nudge) setLiveBubble(null);
                    if (r.nudge && !alreadyDinged(r.nudge.id) && !isOnboardingActive()) {
                        rememberDing(r.nudge.id);
                        playDing();
                    }
                })
                .catch(() => { /* no nudge is the safe reading of a failed check */ });
        check();
        const t = window.setInterval(check, NUDGE_POLL_MS);
        return () => { alive = false; window.clearInterval(t); };
    }, [i18n.language]);
    const [dragging, setDragging] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const wasDragged = useRef(false);

    const handlePointerDown = (e: React.PointerEvent) => {
        setDragging(true);
        wasDragged.current = false;
        dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!dragging) return;
        wasDragged.current = true;
        setPos(onScreen({
            x: e.clientX - dragOffset.current.x,
            y: e.clientY - dragOffset.current.y,
        }));
    };
    /* NEVER OFF THE SCREEN (Guillaume 2026-10-25: "I moved it outside of the
       screen, now I can't see it anymore"). A drag stops at the edges, and a
       smaller window pulls it back in. */
    useEffect(() => {
        const back = () => setPos((p) => onScreen(p));
        back();
        window.addEventListener('resize', back);
        return () => window.removeEventListener('resize', back);
    }, []);

    const handlePointerUp = () => {
        setDragging(false);
    };

    /* The idle float is gone.
       Guillaume, 2026-09-04: "Make the chatbox stop moving, it is slowly going
       up and down." It was a sine wave on translateY, animating forever at 60fps
       whether or not anyone was looking at it. Ambient motion is fine on a logo
       nobody has to use; on the handle for a panel you are reading, it is a
       target that will not hold still, and a repaint every frame for the life of
       the session. */

    // The welcome guide's card is the Orb speaking; a second Orb beside it
    // would sit on the card's buttons on a small screen. Back when it ends.
    if (quiet) return null;

    /* Wherever the Orb sits, what it says opens toward the room left on screen
       (Guillaume 2026-10-24: "regardless of where he is on the page, his messages
       are visible and can't go outside of the page"). From the bottom-right
       corner, the bubble opens to its left and the chat above it, ending at it. */
    const onRight = typeof window !== 'undefined' && pos.x + 48 > window.innerWidth / 2;
    const onBottom = typeof window !== 'undefined' && pos.y + 48 > window.innerHeight / 2;

    return (
        <div
            className="fixed z-50"
            data-orb-float
            style={{
                left: pos.x,
                top: pos.y,
                cursor: dragging ? 'grabbing' : 'grab',
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
            <Popover open={open} onOpenChange={openPanel}>
                <PopoverTrigger asChild>
                    <button
                        className="relative h-24 w-24 rounded-full flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
                        style={{ filter: 'drop-shadow(0 8px 24px rgba(59,130,246,0.25))' }}
                        aria-label={t('orb.assistantLabel')}
                        onClick={(e) => {
                            if (wasDragged.current) e.preventDefault();
                        }}
                    >
                        {/* The login page's Orb, identical (Guillaume, 2026-09-22). */}
                        <div className="pointer-events-none">
                            <LogoOrb className="h-20 w-20" />
                        </div>
                        {nudge ? (
                            // An unread message: a red count, the way every inbox says it.
                            <span
                                className="absolute top-1 end-1 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-semibold leading-5 text-center border-2 border-background"
                                aria-label={t('orb.unreadLabel')}
                            >
                                1
                            </span>
                        ) : interview && !inviteSeen ? (
                            // The interview's one-time invitation: a dot, never the panel.
                            <span className="absolute top-1 end-1 h-4 w-4 rounded-full bg-primary border-2 border-background" />
                        ) : (
                            <span className="absolute bottom-1 end-1 h-4 w-4 rounded-full bg-success border-2 border-background" />
                        )}
                    </button>
                </PopoverTrigger>
                {nudge && !open && (
                    // A short bubble so the dot is not a riddle. The full message is in the chat.
                    <div
                        className={`absolute ${onRight ? "right-24" : "left-24"} ${onBottom ? "bottom-6" : "top-6"} w-56 max-w-[calc(100vw-8rem)] rounded-xl glass-card px-3 py-2 text-xs leading-snug shadow-lg pointer-events-none`}
                        role="status"
                    >
                        {liveBubble ?? t('orb.nudgeBubble')}
                    </div>
                )}
                <PopoverContent align={onRight ? "end" : "start"} side={onBottom ? "top" : "bottom"} collisionPadding={12}
                    className="w-auto p-0 my-3 border-none bg-transparent shadow-none">
                    <OrbChat
                        onClose={() => setOpen(false)}
                        ask={ask}
                        askGuide={askGuide}
                        onAsked={() => setAsk(null)}
                        nudge={nudge}
                        onNudgeShown={() => { setNudge(null); setLiveBubble(null); }}
                        interview={interview}
                        checkIn={checkIn}
                        onInterviewEnd={(done) => {
                            setInterview(false);
                            setCheckIn(null);
                            if (done) setProfileDone(true);
                        }}
                        onUpdateProfile={profileDone && !isViewingAsSomeoneElse() ? requestUpdate : undefined}
                        onCheckInDue={() => {
                            if (isViewingAsSomeoneElse()) return;
                            setCheckIn('self');
                            setInterview(true);
                        }}
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}
